
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Globe, Save, Eye, EyeOff } from 'lucide-react';
import { useSiteConfig } from '@/hooks/useSiteConfig';
import { useSiteConfigValue } from '@/hooks/useSiteConfigValue';
import { useToast } from '@/hooks/use-toast';

const AdminSiteConfigContentSettings = () => {
  const { toast } = useToast();
  const { updateConfiguration } = useSiteConfig();
  
  const { value: siteTitle, loading: siteTitleLoading, refetch: refetchSiteTitle } = useSiteConfigValue('site_title');
  const { value: siteTagline, loading: siteTaglineLoading, refetch: refetchSiteTagline } = useSiteConfigValue('site_tagline');
  const { value: heroTitle, loading: heroTitleLoading, refetch: refetchHeroTitle } = useSiteConfigValue('hero_title');
  const { value: heroSubtitle, loading: heroSubtitleLoading, refetch: refetchHeroSubtitle } = useSiteConfigValue('hero_subtitle');
  const { value: heroCTA, loading: heroCTALoading, refetch: refetchHeroCTA } = useSiteConfigValue('hero_cta_text');
  const { value: announcementEnabled, loading: announcementEnabledLoading, refetch: refetchAnnouncementEnabled } = useSiteConfigValue('announcement_enabled');
  const { value: announcementText, loading: announcementTextLoading, refetch: refetchAnnouncementText } = useSiteConfigValue('announcement_banner');
  const { value: demoVideoUrl, loading: demoVideoUrlLoading, refetch: refetchDemoVideoUrl } = useSiteConfigValue('demo_video_url');
  const { value: demoVideoTitle, loading: demoVideoTitleLoading, refetch: refetchDemoVideoTitle } = useSiteConfigValue('demo_video_title');
  
  const [saving, setSaving] = useState(false);
  const [tempValues, setTempValues] = useState({
    title: '',
    tagline: '',
    heroTitle: '',
    heroSubtitle: '',
    heroCTA: '',
    announcementEnabled: '',
    announcementText: '',
    demoVideoUrl: '',
    demoVideoTitle: ''
  });

  React.useEffect(() => {
    if (siteTitle && !tempValues.title) setTempValues(prev => ({ ...prev, title: siteTitle }));
    if (siteTagline && !tempValues.tagline) setTempValues(prev => ({ ...prev, tagline: siteTagline }));
    if (heroTitle && !tempValues.heroTitle) setTempValues(prev => ({ ...prev, heroTitle: heroTitle }));
    if (heroSubtitle && !tempValues.heroSubtitle) setTempValues(prev => ({ ...prev, heroSubtitle: heroSubtitle }));
    if (heroCTA && !tempValues.heroCTA) setTempValues(prev => ({ ...prev, heroCTA: heroCTA }));
    if (announcementEnabled && !tempValues.announcementEnabled) setTempValues(prev => ({ ...prev, announcementEnabled: announcementEnabled }));
    if (announcementText && !tempValues.announcementText) setTempValues(prev => ({ ...prev, announcementText: announcementText }));
    if (demoVideoUrl && !tempValues.demoVideoUrl) setTempValues(prev => ({ ...prev, demoVideoUrl: demoVideoUrl }));
    if (demoVideoTitle && !tempValues.demoVideoTitle) setTempValues(prev => ({ ...prev, demoVideoTitle: demoVideoTitle }));
  }, [siteTitle, siteTagline, heroTitle, heroSubtitle, heroCTA, announcementEnabled, announcementText, demoVideoUrl, demoVideoTitle, tempValues]);

  const handleUpdate = async (key: string, value: string, refetch: () => void) => {
    setSaving(true);
    try {
      await updateConfiguration(key, value);
      setTimeout(() => refetch(), 1000);
      toast({
        title: "Content Setting Updated",
        description: `${key} has been updated successfully.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update content setting.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const loading = siteTitleLoading || siteTaglineLoading || heroTitleLoading || heroSubtitleLoading || heroCTALoading || announcementEnabledLoading || announcementTextLoading || demoVideoUrlLoading || demoVideoTitleLoading;

  if (loading) {
    return (
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center space-x-2">
            <Globe className="w-5 h-5" />
            <span>Website Content Settings</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-white/10 rounded w-1/3"></div>
            <div className="h-10 bg-white/10 rounded w-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white/10 backdrop-blur-md border-white/20">
      <CardHeader>
        <CardTitle className="text-white flex items-center space-x-2">
          <Globe className="w-5 h-5" />
          <span>Website Content Settings</span>
          <Badge className="bg-blue-600/20 text-blue-300 border-blue-500/30">
            Site Identity
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Site Title */}
        <div className="p-4 rounded-lg bg-white/5 border border-white/10">
          <Label className="text-white">Site Title</Label>
          <div className="flex space-x-2 mt-2">
            <Input
              value={tempValues.title}
              onChange={(e) => setTempValues(prev => ({ ...prev, title: e.target.value }))}
              disabled={saving}
              placeholder="Background Generator"
              className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            />
            <Button
              onClick={() => handleUpdate('site_title', tempValues.title, refetchSiteTitle)}
              disabled={saving || !tempValues.title.trim() || tempValues.title === siteTitle}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Save className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-sm text-gray-400 mt-2">Main website title displayed in browser tab</p>
        </div>

        {/* Site Tagline */}
        <div className="p-4 rounded-lg bg-white/5 border border-white/10">
          <Label className="text-white">Site Tagline</Label>
          <div className="flex space-x-2 mt-2">
            <Input
              value={tempValues.tagline}
              onChange={(e) => setTempValues(prev => ({ ...prev, tagline: e.target.value }))}
              disabled={saving}
              placeholder="Create stunning backgrounds with AI"
              className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            />
            <Button
              onClick={() => handleUpdate('site_tagline', tempValues.tagline, refetchSiteTagline)}
              disabled={saving || tempValues.tagline === siteTagline}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Save className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-sm text-gray-400 mt-2">Website subtitle/tagline</p>
        </div>

        {/* Hero Section */}
        <div className="p-4 rounded-lg bg-white/5 border border-white/10">
          <h4 className="text-white font-medium mb-4">Hero Section Content</h4>
          
          <div className="space-y-4">
            <div>
              <Label className="text-white">Hero Title</Label>
              <div className="flex space-x-2 mt-2">
                <Input
                  value={tempValues.heroTitle}
                  onChange={(e) => setTempValues(prev => ({ ...prev, heroTitle: e.target.value }))}
                  disabled={saving}
                  placeholder="Generate Amazing Backgrounds"
                  className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                />
                <Button
                  onClick={() => handleUpdate('hero_title', tempValues.heroTitle, refetchHeroTitle)}
                  disabled={saving || tempValues.heroTitle === heroTitle}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Save className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-white">Hero Subtitle</Label>
              <div className="flex space-x-2 mt-2">
                <Textarea
                  value={tempValues.heroSubtitle}
                  onChange={(e) => setTempValues(prev => ({ ...prev, heroSubtitle: e.target.value }))}
                  disabled={saving}
                  placeholder="AI-powered background generation for your creative projects"
                  className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                />
                <Button
                  onClick={() => handleUpdate('hero_subtitle', tempValues.heroSubtitle, refetchHeroSubtitle)}
                  disabled={saving || tempValues.heroSubtitle === heroSubtitle}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Save className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-white">Hero CTA Button</Label>
              <div className="flex space-x-2 mt-2">
                <Input
                  value={tempValues.heroCTA}
                  onChange={(e) => setTempValues(prev => ({ ...prev, heroCTA: e.target.value }))}
                  disabled={saving}
                  placeholder="Start Creating"
                  className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                />
                <Button
                  onClick={() => handleUpdate('hero_cta_text', tempValues.heroCTA, refetchHeroCTA)}
                  disabled={saving || tempValues.heroCTA === heroCTA}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Save className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Announcement Banner */}
        <div className="p-4 rounded-lg bg-white/5 border border-white/10">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-white">Announcement Banner</Label>
            <div className="flex items-center space-x-2">
              <Switch
                checked={tempValues.announcementEnabled === 'true'}
                onCheckedChange={(checked) => {
                  const newValue = checked.toString();
                  setTempValues(prev => ({ ...prev, announcementEnabled: newValue }));
                  handleUpdate('announcement_enabled', newValue, refetchAnnouncementEnabled);
                }}
                disabled={saving}
              />
              {tempValues.announcementEnabled === 'true' ? <Eye className="w-4 h-4 text-green-400" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
            </div>
          </div>
          
          <div className="flex space-x-2 mt-2">
            <Textarea
              value={tempValues.announcementText}
              onChange={(e) => setTempValues(prev => ({ ...prev, announcementText: e.target.value }))}
              disabled={saving}
              placeholder="Important announcement..."
              className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            />
            <Button
              onClick={() => handleUpdate('announcement_banner', tempValues.announcementText, refetchAnnouncementText)}
              disabled={saving || tempValues.announcementText === announcementText}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Save className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-sm text-gray-400 mt-2">Site-wide announcement displayed at top</p>
        </div>

        {/* Demo Video Settings */}
        <div className="p-4 rounded-lg bg-white/5 border border-white/10">
          <h4 className="text-white font-medium mb-4">Demo Video Settings</h4>
          
          <div className="space-y-4">
            <div>
              <Label className="text-white">Demo Video URL</Label>
              <div className="flex space-x-2 mt-2">
                <Input
                  value={tempValues.demoVideoUrl}
                  onChange={(e) => setTempValues(prev => ({ ...prev, demoVideoUrl: e.target.value }))}
                  disabled={saving}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                />
                <Button
                  onClick={() => handleUpdate('demo_video_url', tempValues.demoVideoUrl, refetchDemoVideoUrl)}
                  disabled={saving || tempValues.demoVideoUrl === demoVideoUrl}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Save className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-sm text-gray-400 mt-2">YouTube URL for the demo video</p>
            </div>

            <div>
              <Label className="text-white">Demo Video Title</Label>
              <div className="flex space-x-2 mt-2">
                <Input
                  value={tempValues.demoVideoTitle}
                  onChange={(e) => setTempValues(prev => ({ ...prev, demoVideoTitle: e.target.value }))}
                  disabled={saving}
                  placeholder="Product Demo"
                  className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                />
                <Button
                  onClick={() => handleUpdate('demo_video_title', tempValues.demoVideoTitle, refetchDemoVideoTitle)}
                  disabled={saving || tempValues.demoVideoTitle === demoVideoTitle}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Save className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-sm text-gray-400 mt-2">Title displayed in demo video modal</p>
            </div>
          </div>
        </div>

        {/* Debug Info */}
        <div className="text-xs text-gray-500 p-3 bg-black/20 rounded">
          <div>Title: {siteTitle}</div>
          <div>Tagline: {siteTagline}</div>
          <div>Hero Title: {heroTitle}</div>
          <div>Announcement Enabled: {announcementEnabled}</div>
          <div>Demo Video URL: {demoVideoUrl}</div>
          <div>Demo Video Title: {demoVideoTitle}</div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminSiteConfigContentSettings;
